'use client';

import { useEffect, useState } from 'react';
import {
  isCampaignWindowLive,
  type IScheduledCampaign,
} from '@/lib/scheduledCampaign';

/**
 * Client-side live check for a date-windowed campaign singleton.
 *
 * The pages are ISR-cached, so a server-side Date.now() would be frozen into
 * the HTML — instead the state starts as `false` (matching whatever the cached
 * markup shows, avoiding hydration mismatches) and is re-evaluated on an
 * interval. An open tab therefore switches to the campaign state shortly after
 * the start boundary and back at the end boundary, with no publish involved.
 *
 * QA/preview override (only honoured while the campaign is enabled, so it is
 * inert in normal operation): append `?<previewParam>=1` to force the live
 * state before the start date, or `?<previewParam>=0` to force the standard
 * state while live. Lets marketing verify on /, /de and /br without
 * publishing anything.
 */
export function useCampaignLive(
  config: IScheduledCampaign | null | undefined,
  previewParam: string,
  checkIntervalMs: number,
): boolean {
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (!config?.isEnabled) {
      setLive(false);
      return;
    }

    const preview = new URLSearchParams(window.location.search).get(previewParam);
    if (preview === '1' || preview === '0') {
      setLive(preview === '1');
      return;
    }

    const check = () => setLive(isCampaignWindowLive(config));
    check();

    // Background tabs are throttled and sleeping devices stop timers
    // altogether, so re-check as soon as the page becomes visible again
    // instead of waiting out the interval.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const interval = setInterval(check, checkIntervalMs);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [config, previewParam, checkIntervalMs]);

  return live;
}
